import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface Ticket {
  id: string;
  name: string;
  destination: string;
  category: string;
  price: number;
  originalPrice?: number;
  provider: string;
  rating: number;
  features: string[];
  popular?: boolean;
  savings?: number;
}

export default function TicketFinder() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDestination, setSelectedDestination] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('popular');

  const tickets: Ticket[] = [
    {
      id: '1',
      name: 'Tokyo Disneyland 1-Day Pass',
      destination: 'Tokyo, Japan',
      category: 'Theme Parks',
      price: 79,
      originalPrice: 85,
      provider: 'Klook',
      rating: 4.8,
      features: ['Skip-the-line', 'Mobile ticket', 'Instant confirmation'],
      popular: true,
      savings: 6
    },
    {
      id: '2',
      name: 'Universal Studios Japan',
      destination: 'Osaka, Japan',
      category: 'Theme Parks',
      price: 72,
      provider: 'KKday',
      rating: 4.9,
      features: ['Express Pass available', 'Mobile ticket', 'Free cancellation'],
      popular: true
    },
    {
      id: '3',
      name: 'teamLab Borderless Tokyo',
      destination: 'Tokyo, Japan',
      category: 'Museums',
      price: 32,
      originalPrice: 38,
      provider: 'Official',
      rating: 4.7,
      features: ['Timed entry', 'Mobile ticket', 'Photo-friendly'],
      savings: 6
    },
    {
      id: '4',
      name: 'Tokyo Skytree Fast Ticket',
      destination: 'Tokyo, Japan',
      category: 'Observation Decks',
      price: 28,
      provider: 'Klook',
      rating: 4.6,
      features: ['Skip-the-line', 'Combo deals available', 'Mobile ticket']
    },
    {
      id: '5',
      name: 'Gardens by the Bay',
      destination: 'Singapore',
      category: 'Attractions',
      price: 25,
      originalPrice: 30,
      provider: 'Pelago',
      rating: 4.8,
      features: ['Cloud Forest + Flower Dome', 'Mobile ticket', 'Valid for 30 days'],
      popular: true,
      savings: 5
    },
    {
      id: '6',
      name: 'Universal Studios Singapore',
      destination: 'Singapore',
      category: 'Theme Parks',
      price: 68,
      provider: 'Klook',
      rating: 4.7,
      features: ['Express Pass available', 'Mobile ticket', 'Same-day entry']
    },
    {
      id: '7',
      name: 'Hong Kong Disneyland',
      destination: 'Hong Kong',
      category: 'Theme Parks',
      price: 75,
      originalPrice: 82,
      provider: 'KKday',
      rating: 4.6,
      features: ['2-Day option', 'Mobile ticket', 'Free shuttle bus'],
      savings: 7
    },
    {
      id: '8',
      name: 'Ocean Park Hong Kong',
      destination: 'Hong Kong',
      category: 'Theme Parks',
      price: 42,
      provider: 'Klook',
      rating: 4.5,
      features: ['Cable car included', 'Marine life + rides', 'Mobile ticket'],
      popular: true
    },
    {
      id: '9',
      name: 'Walt Disney World Multi-Day',
      destination: 'Orlando, USA',
      category: 'Theme Parks',
      price: 449,
      originalPrice: 499,
      provider: 'Official',
      rating: 4.9,
      features: ['4-Park access', 'Park hopper option', 'Lightning Lane available'],
      popular: true,
      savings: 50
    },
    {
      id: '10',
      name: 'Disneyland California 1-Day',
      destination: 'Anaheim, USA',
      category: 'Theme Parks',
      price: 104,
      provider: 'Official',
      rating: 4.8,
      features: ['Both parks option', 'Genie+ available', 'Mobile ticket']
    },
    {
      id: '11',
      name: 'Kyoto Arashiyama Bamboo Forest Tour',
      destination: 'Kyoto, Japan',
      category: 'Tours & Experiences',
      price: 45,
      provider: 'Viator',
      rating: 4.7,
      features: ['Guided tour', 'Monkey park visit', 'Small group']
    },
    {
      id: '12',
      name: 'Singapore Zoo with Tram',
      destination: 'Singapore',
      category: 'Zoos & Aquariums',
      price: 35,
      originalPrice: 40,
      provider: 'Klook',
      rating: 4.8,
      features: ['Unlimited tram rides', 'Mobile ticket', 'Combo deals available'],
      savings: 5
    }
  ];

  const destinations = ['Tokyo, Japan', 'Osaka, Japan', 'Kyoto, Japan', 'Singapore', 'Hong Kong', 'Orlando, USA', 'Anaheim, USA'];
  const categories = ['Theme Parks', 'Museums', 'Observation Decks', 'Zoos & Aquariums', 'Attractions', 'Tours & Experiences'];

  const filteredTickets = tickets
    .filter(ticket => {
      const matchesSearch = ticket.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          ticket.destination.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesDestination = selectedDestination === 'all' || ticket.destination === selectedDestination;
      const matchesCategory = selectedCategory === 'all' || ticket.category === selectedCategory;
      return matchesSearch && matchesDestination && matchesCategory;
    })
    .sort((a, b) => {
      if (sortBy === 'price-low') return a.price - b.price;
      if (sortBy === 'price-high') return b.price - a.price;
      if (sortBy === 'rating') return b.rating - a.rating;
      if (sortBy === 'popular') return (b.popular ? 1 : 0) - (a.popular ? 1 : 0);
      return 0;
    });

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAFA] to-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/tools" className="text-[#4A90E2] hover:text-[#FF6B35] transition-colors mb-4 inline-block">
            ← Back to Tools
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold text-[#0A2540] mb-4">
            Ticket Finder
          </h1>
          <p className="text-gray-600 text-lg">
            Find the best deals on attraction tickets and experiences
          </p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="grid md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search attractions
              </label>
              <input
                type="text"
                placeholder="Search by name or destination..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent transition-all"
              />
            </div>

            {/* Destination Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Destination
              </label>
              <select
                value={selectedDestination}
                onChange={(e) => setSelectedDestination(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent transition-all"
              >
                <option value="all">All Destinations</option>
                {destinations.map(dest => (
                  <option key={dest} value={dest}>{dest}</option>
                ))}
              </select>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent transition-all"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Sort Options */}
          <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
            <div className="text-sm text-gray-600">
              Showing {filteredTickets.length} {filteredTickets.length === 1 ? 'result' : 'results'}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent transition-all"
              >
                <option value="popular">Most Popular</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results */}
        {filteredTickets.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-[#0A2540] mb-2">No tickets found</h3>
            <p className="text-gray-600">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTickets.map((ticket) => (
              <div
                key={ticket.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col"
              >
                {/* Card Header */}
                <div className="bg-gradient-to-r from-[#4A90E2] to-[#0A2540] p-4 text-white">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-xs font-semibold bg-white/20 px-2 py-1 rounded">
                      {ticket.category}
                    </div>
                    {ticket.popular && (
                      <div className="text-xs font-semibold bg-[#FF6B35] px-2 py-1 rounded">
                        🔥 Popular
                      </div>
                    )}
                  </div>
                  <h3 className="text-lg font-bold mb-1">{ticket.name}</h3>
                  <div className="text-xs opacity-90">📍 {ticket.destination}</div>
                </div>

                {/* Card Body */}
                <div className="p-4 flex-grow">
                  {/* Rating and Provider */}
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-1">
                      <span className="text-yellow-500">⭐</span>
                      <span className="text-sm font-semibold">{ticket.rating}</span>
                    </div>
                    <div className="text-xs text-gray-500">via {ticket.provider}</div>
                  </div>

                  {/* Features */}
                  <div className="mb-4">
                    {ticket.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs text-gray-600 mb-1">
                        <span className="text-green-500">✓</span>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Price */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-end justify-between">
                      <div>
                        {ticket.originalPrice && (
                          <div className="text-xs text-gray-500 line-through">
                            ${ticket.originalPrice}
                          </div>
                        )}
                        <div className="text-2xl font-bold text-[#0A2540]">
                          ${ticket.price}
                        </div>
                        {ticket.savings && (
                          <div className="text-xs text-green-600 font-semibold">
                            Save ${ticket.savings}
                          </div>
                        )}
                      </div>
                      <button className="bg-[#FF6B35] hover:bg-[#e55a2a] text-white px-4 py-2 rounded-lg font-semibold transition-colors text-sm">
                        View Deal
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info Banner */}
        <div className="mt-8 bg-gradient-to-r from-[#4A90E2]/10 to-[#FF6B35]/10 border border-[#4A90E2]/30 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <span className="text-3xl">💳</span>
            <div>
              <h3 className="text-lg font-semibold text-[#0A2540] mb-2">
                Best Price Guarantee
              </h3>
              <p className="text-gray-700 text-sm mb-3">
                We compare prices from multiple providers to ensure you get the best deal on your tickets.
                Most tickets include instant confirmation and mobile delivery.
              </p>
              <div className="flex flex-wrap gap-4 text-xs text-gray-600">
                <div className="flex items-center gap-1">
                  <span className="text-green-500">✓</span>
                  <span>Free cancellation available</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-green-500">✓</span>
                  <span>Instant confirmation</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-green-500">✓</span>
                  <span>24/7 customer support</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
